import React, { useEffect, useMemo, useState } from "react";
import { getAllNextMilestones, getProjects } from "../../data/projectsClient.js";
import { T } from "../../app/tokens.js";
import { buildOfficeProjectsActivityModel } from "../../v2/pulse/portfolioMotion.js";
import { isTerminalProject } from "../../lib/projectStage.js";
import { ProjectStagePill } from "../projects/ProjectStageControl.jsx";

function StatusChip({ row }) {
  return (
    <span
      data-testid={`office-project-status-${row.id}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        borderRadius: 999,
        padding: "3px 10px",
        background: row.statusBg,
        color: row.statusColor,
        fontSize: T.fsXs,
        fontWeight: T.fwBold,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: row.statusColor }} />
      {row.statusLabel}
    </span>
  );
}

export function OfficeProjectsActivityPane({ locationId, onViewAll, onOpenProject } = {}) {
  const [state, setState] = useState({
    loading: true,
    projects: [],
    milestones: [],
    projectsFailed: false,
  });

  useEffect(() => {
    let cancelled = false;
    setState((current) => ({ ...current, loading: true, projectsFailed: false }));

    Promise.allSettled([
      Promise.resolve().then(() => getProjects()),
      Promise.resolve().then(() => getAllNextMilestones()),
    ]).then(([projectsResult, milestonesResult]) => {
      if (cancelled) return;
      setState({
        loading: false,
        projects: projectsResult.status === "fulfilled" && Array.isArray(projectsResult.value)
          ? projectsResult.value
          : [],
        milestones: milestonesResult.status === "fulfilled" && Array.isArray(milestonesResult.value)
          ? milestonesResult.value
          : [],
        projectsFailed: projectsResult.status === "rejected",
      });
    });

    return () => { cancelled = true; };
  }, [locationId]);

  const model = useMemo(
    () => buildOfficeProjectsActivityModel({
      projects: state.projects,
      milestones: state.milestones,
      locationId,
      limit: Number.MAX_SAFE_INTEGER,
    }),
    [state.projects, state.milestones, locationId],
  );
  const past = useMemo(() => state.projects.filter(p => !p.deletedAt && p.locationId === locationId && isTerminalProject(p)), [state.projects, locationId]);

  return (
    <section
      data-testid="office-projects-activity-pane"
      aria-labelledby="office-projects-activity-title"
      style={{
        // Task #8581: compact half-width row-3 card beside What's ahead.
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        boxShadow: T.cardShadow,
        padding: "0 20px 14px",
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, minHeight: 40 }}>
        <h2
          id="office-projects-activity-title"
          style={{ display: "flex", alignItems: "center", gap: 9, margin: 0, color: T.dark, fontSize: T.fsBase, fontWeight: T.fwBold }}
        >
          <span aria-hidden="true" style={{ width: 3, height: 15, borderRadius: 2, background: T.blue }} />
          Projects &amp; activity
        </h2>
        {!state.loading && !state.projectsFailed && (
          <span data-testid="office-projects-count" style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold }}>
            {model.projectCount} current
            {model.atRiskCount > 0 && (
              <span data-testid="office-projects-at-risk" style={{ color: T.ragAmber, fontWeight: T.fwBold }}>
                {" · "}{model.atRiskCount} at risk
              </span>
            )}
          </span>
        )}
      </div>

      {state.loading ? (
        <div data-testid="office-projects-loading" style={{ minHeight: 64, display: "grid", placeItems: "center", color: T.mid, fontSize: T.fsSm5 }}>
          Loading project activity…
        </div>
      ) : state.projectsFailed ? (
        <div data-testid="office-projects-error" style={{ minHeight: 64, display: "grid", placeItems: "center", color: T.mid, fontSize: T.fsSm5 }}>
          Project activity is temporarily unavailable.
        </div>
      ) : model.projectCount === 0 && past.length === 0 ? (
        <div data-testid="office-projects-empty" style={{ color: T.light, fontSize: T.fsSm5, fontStyle: "italic", padding: "6px 0 2px" }}>
          No active projects at this office
        </div>
      ) : (
        <>
          <ul aria-label="Office projects and activity" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {model.rows.map((row, idx) => (
              <li
                key={row.id}
                data-testid={`office-project-row-${row.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "4px 12px",
                  padding: "8px 0",
                  borderTop: idx > 0 ? `1px solid ${T.hairline}` : 0,
                }}
              >
                <div style={{ flex: "1 1 160px", minWidth: 0 }}>
                  {typeof onOpenProject === "function" ? (
                    <button type="button" data-testid={`office-project-open-${row.id}`} onClick={() => onOpenProject(row.id)}
                      style={{ border: 0, background: "transparent", padding: 0, textAlign: "left", color: T.blue, fontSize: T.fsSm5, fontWeight: T.fwBold, overflowWrap: "anywhere", cursor: "pointer", fontFamily: "inherit" }}>
                      {row.projectName}
                    </button>
                  ) : (
                    <div style={{ color: T.dark, fontSize: T.fsSm5, fontWeight: T.fwBold, overflowWrap: "anywhere" }}>{row.projectName}</div>
                  )}
                  <div data-testid={`office-project-milestone-${row.id}`} style={{ marginTop: 1, color: T.mid, fontSize: T.fsXs, overflowWrap: "anywhere" }}>
                    {row.milestoneName}
                    {row.milestoneDate && <span style={{ fontWeight: T.fwSemibold }}>{" · "}{row.milestoneDate}</span>}
                  </div>
                </div>
                {state.projects.find(p => p.id === row.id)?.stage === "active" ? <StatusChip row={row} />
                  : <ProjectStagePill stage={state.projects.find(p => p.id === row.id)?.stage} />}
              </li>
            ))}
          </ul>
          {past.length > 0 && <section aria-label="Past projects" data-testid="office-projects-past">
            <h3 style={{ fontSize: T.fsSm, color: T.mid }}>Past</h3>
            {past.map(project => <div key={project.id} style={{ padding: "8px 0", display: "flex", justifyContent: "space-between", gap: 12 }}>
              <button type="button" onClick={() => onOpenProject?.(project.id)}
                style={{ border: 0, background: "none", color: T.blue, fontFamily: "inherit", cursor: "pointer" }}>{project.name}</button>
              <ProjectStagePill stage={project.stage} />
            </div>)}
          </section>}
          <div style={{ marginTop: 8, textAlign: "right" }}>
            <button
              type="button"
              data-testid="office-projects-view-all"
              onClick={onViewAll}
              style={{ border: 0, background: "transparent", color: T.blue, fontSize: T.fsSm5, fontWeight: T.fwBold, cursor: "pointer" }}
            >
              View all in Projects →
            </button>
          </div>
        </>
      )}
    </section>
  );
}